import { ApolloClient, NormalizedCacheObject } from "@apollo/client"
import { LoadingButton, TabContext, TabPanel } from "@mui/lab"
import { Box, Button, Dialog, DialogContent, FormControl, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useNavigate, useParams } from "react-router"
import { GetOrganisationProfile, UpdateLegalDetails, UpdateOrganisationDetails } from "../../api/orgs"
import { Loader } from "../../components/Loader"
import { useAppContext } from "../../contexts/AppContext"
import { checkEmail, isNullOrEmpty, showErrorMessage, showSuccessMessage } from "../../helper/helper"
import { GroupModel } from "../../models/organisation/GroupModel"
import { TaxInformationModel } from "../../models/organisation/TaxInformationModel"
import { Organisation } from "../../models/organisation/Organisation"
import { ReactComponent as Warning } from "../../rsc/ic_warning.svg"
import { LocationPicker } from "../profil/LocationPicker"
import { OrgProfileGroup } from "./OrgProfileGroup"
import { t } from "i18next"

type OrganisationProfileProps = {
    client: ApolloClient<NormalizedCacheObject>,
}

type TransactionType = {
    label: string
    value: string
}

const TransactionsList: TransactionType[] = [
    { value: "NON_TAXPAYERSTATEMILL", label: t('tax_03_label') },
    { value: "TAXPAYER_VAT_11", label: t('tax_04_label') },
    { value: "TAXPAYER", label: t("tax_05_label") },
    { value: "TAX_EXEMPT", label: t("tax_07_label") }
]

export default function OrganisationProfile(props: OrganisationProfileProps) {

    const { id } = useParams()

    const { t } = useTranslation()

    const [taxInfo, setTaxInfo] = useState<TaxInformationModel | null>(null)
    const [orgName, setOrgName] = useState<string>(null)
    const [showPickUpLocation, setShowPickupLocation] = useState(false)
    const [saving, setSaving] = useState(false)
    const [tabIndex, setTabIndex] = useState<string>("1")
    const [groups, setGroups] = useState<GroupModel[]>([])
    const [org, setOrg] = useState<Organisation>(undefined)

    const pattern = "xx.xxx.xxx.x-xxx.xxx"

    const { millSelected } = useAppContext()
    const navigate = useNavigate()
    const previousMillId = useRef(millSelected?.id)

    // Reached from Mill settings, the page shows the selected mill, so it follows
    // the mill picker. An admin viewing another organisation is left where they are.
    useEffect(() => {
        const previous = previousMillId.current
        previousMillId.current = millSelected?.id
        if (!millSelected?.id || previous === undefined || previous === millSelected.id) return
        if (String(previous) === id) {
            navigate(`/explorer/company_profil/${millSelected.id}`, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [millSelected?.id])

    useEffect(() => {
        // A slower response for the previous id must not overwrite the current one.
        let cancelled = false
        props.client.query({
            query: GetOrganisationProfile,
            variables: {
                organisationId: parseInt(id),
                economics: { organisation_id: parseInt(id) }
            },
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then(result => {
            const organisation = result.data.org?.organisation
            if (!cancelled && organisation) {
                const legal = result.data.economics?.legal_details
                // Flatten the gateway shape onto the keys Organisation.build expects.
                const org = Organisation.build({
                    ...organisation,
                    organisation_type: organisation.type,
                    buyer: result.data.economics?.buyer,
                    legal_name: legal?.name,
                    tax_identification_number: legal?.vat_number,
                    vat_transaction_type: legal?.transaction_type,
                    address: legal?.address,
                    email: legal?.email,
                    phonenumber: legal?.phone_number,
                    postal_code: legal?.postal_code,
                    rt: legal?.rt,
                    rw: legal?.rw,
                    legal_location: legal?.location
                })
                setGroups(GroupModel.buildGroups(result.data.groups))
                setOrgName(org.name)
                setTaxInfo(org.taxInformations)
                setOrg(org)
            }
        })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    function updateOrganisationTaxInformation() {
        setSaving(true)
        props.client.mutate({
            mutation: UpdateLegalDetails,
            variables: {
                input: {
                    organisation_id: parseInt(id),
                    name: taxInfo?.legal_name,
                    vat_number: taxInfo?.taxNumber,
                    transaction_type: taxInfo?.vat_transaction_type ?? TransactionsList[0].value,
                    address: taxInfo?.address,
                    billing_address: taxInfo?.billing_address,
                    email: taxInfo?.email,
                    phone_number: taxInfo?.phoneNumber,
                    postal_code: taxInfo?.postalCode,
                    rt: taxInfo?.rt,
                    rw: taxInfo?.rw,
                    location_id: taxInfo?.location?.id
                }
            },
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then(data => {
            if (data.data?.update_legal_details === null) {
                showErrorMessage(t('incorrect_role'))
            } else {
                showSuccessMessage(t('modification_saved'))
            }
        }).catch((e: TypeError) => {
            showErrorMessage(e)
        })
            .finally(() => {
                setSaving(false)
            })
    }

    function updateOrganisationInformation() {
        setSaving(true)
        props.client.mutate({
            mutation: UpdateOrganisationDetails,
            variables: {
                input: {
                    organisation_id: parseInt(id),
                    name: orgName
                }
            }
        }).then(data => {
            if (data.data?.update_organisation === null) {
                showErrorMessage(t('incorrect_role'))
            } else {
                showSuccessMessage(t('modification_saved'))
            }
        }).catch((e: TypeError) => {
            showErrorMessage(e)
        })
            .finally(() => {
                setSaving(false)
            })
    }


    function isDigit(char: string) {
        return /^\d$/.test(char);
    }

    function formatVatString(value?: string): string {
        if (value?.length >= 20) {
            return value
        }
        if (value) {
            var digitString = ""
            for (let index = 0; index < value.length; index++) {
                const element = value[index];
                if (isDigit(element)) {
                    digitString += element
                }
            }
            var res = ""
            var pi = 0
            for (let index = 0; index < digitString.length; index++) {
                if (pattern[pi] === 'x') {
                    res += digitString[index].toString()
                    pi += 1
                } else {
                    res += pattern[pi] + digitString[index].toString()
                    pi += 2
                }
            }
            return res
        }
        return ""
    }

    function areFieldsValid(): boolean {
        if (taxInfo.taxNumber?.length < 20) {
            return false
        }
        if (isNullOrEmpty(taxInfo?.legal_name)) {
            return false
        }

        if (isNullOrEmpty(taxInfo?.email) || checkEmail(taxInfo?.email) === null) {
            return false
        }

        if (isNullOrEmpty(taxInfo?.address)) {
            return false
        }
        if (isNullOrEmpty(taxInfo?.rt)) {
            return false
        }
        if (isNullOrEmpty(taxInfo?.rw)) {
            return false
        }
        if (isNullOrEmpty(taxInfo?.phoneNumber)) {
            return false
        }
        if (isNullOrEmpty(taxInfo?.postalCode)) {
            return false
        }

        if (taxInfo?.location === null || taxInfo?.location === undefined) {
            return false
        }
        return true
    }

    const fieldLayoutStyles = "flex flex-row items-center gap-[10px] max-md:flex-col max-md:items-start"
    const labelStyle = "flex items-center shrink-0 w-[200px]"
    const fieldStyle = "w-[300px]"

    return (
        <div className="pl-[28px] pr-[28px] pb-[20px]">
            <TabContext value={tabIndex}>
                <Box marginTop={"20px"} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v.toString())} aria-label="basic tabs example">
                        <Tab label={t('profile')} value={"1"} />
                        <Tab label={t('tax')} value={"2"} />
                        <Tab label={t('prices_visibility')} value={"3"} />
                    </Tabs>
                </Box>
                <TabPanel value={"1"}>
                    {
                        orgName ?
                            <div>
                                <div className="flex flex-col gap-[30px] mt-[40px] w-[40%]">
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('name')}</p>
                                        <TextField className={fieldStyle} value={orgName} onChange={e => setOrgName(e.target.value)} />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-[30px]" >
                                    <LoadingButton variant="contained" loading={saving} onClick={() => updateOrganisationInformation()}>{t('save')}</LoadingButton>
                                </div>
                            </div>
                            : <></>
                    }
                </TabPanel>
                <TabPanel value={"2"}>
                    {
                        taxInfo ?
                            <div>
                                <div className='yellowLightBgColor mt-[40px] mb-[40px] p-[20px]' style={{ color: "black", textAlign: "center", display: `${areFieldsValid() ? "none" : "block"}` }}>
                                    <Warning className='inline-block h-[30px] w-[auto]' />
                                    <p className='inline-block ml-[15px] align-middle'>{t('all_fields_requiered')}</p>
                                </div>
                                <div className="flex flex-col gap-[10px] mt-[40px]">
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('legal_company_name')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.legal_name ?? ''}
                                            onChange={e => { taxInfo.legal_name = e.target.value; setTaxInfo(TaxInformationModel.copy(taxInfo)) }} />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('vat_number')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.taxNumber ?? ''}
                                            inputProps={{ maxLength: 20 }}
                                            onChange={e => {
                                                taxInfo.taxNumber = formatVatString(e.target.value)
                                                setTaxInfo(TaxInformationModel.copy(taxInfo))
                                            }
                                            } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('transaction_type')}</p>
                                        <FormControl style={{ marginTop: "4px" }} className="w-[200px]">
                                            <Select
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        paddingRight: 4,
                                                        paddingLeft: 2,
                                                        paddingTop: 1,
                                                        paddingBottom: 1,
                                                    }
                                                }}
                                                displayEmpty={true}
                                                defaultValue={taxInfo?.vat_transaction_type ?? TransactionsList[0].value}
                                                onChange={e => {
                                                    taxInfo.vat_transaction_type = e.target.value
                                                    setTaxInfo(TaxInformationModel.copy(taxInfo))
                                                }}>
                                                {
                                                    TransactionsList.map(o => {
                                                        return (
                                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('company_address')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.address ?? ''} onChange={e => {
                                            taxInfo.address = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('rt')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.rt ?? ''} onChange={e => {
                                            taxInfo.rt = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('rw')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.rw ?? ''} onChange={e => {
                                            taxInfo.rw = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('billing_email')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.email ?? ''} onChange={e => {
                                            taxInfo.email = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('billing_address')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.billing_address ?? ''} onChange={e => {
                                            taxInfo.billing_address = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('phone_number')}</p>
                                        <div className={fieldStyle}>
                                            <PhoneInput country="US" value={taxInfo?.phoneNumber ?? ''} onChange={e => {
                                                taxInfo.phoneNumber = e
                                                setTaxInfo(TaxInformationModel.copy(taxInfo))
                                            }
                                            } />
                                        </div>
                                    </div>
                                    <div className={fieldLayoutStyles}>
                                        <p className={labelStyle}>{t('postal_code')}</p>
                                        <TextField className={fieldStyle} value={taxInfo?.postalCode ?? ''} onChange={e => {
                                            taxInfo.postalCode = e.target.value
                                            setTaxInfo(TaxInformationModel.copy(taxInfo))
                                        }
                                        } />
                                    </div>
                                </div>
                                <div className="flex max-w-[600px]">
                                    <Box sx={{ boxShadow: 3, marginTop: "20px", padding: "30px" }}>
                                        <div className="flex flex-row gap-[30px] flex-wrap">
                                            <div className="flex flex-col w-[40%]">
                                                <p className="mb-[10px]">{t('village')}</p>
                                                <TextField placeholder={t('village')} disabled={true} value={taxInfo?.location?.village ?? ''} />
                                            </div>
                                            <div className="flex flex-col w-[40%]">
                                                <p className="mb-[10px]">{t('district')}</p>
                                                <TextField placeholder={t('district')} disabled={true} value={taxInfo?.location?.district ?? ''} />
                                            </div>
                                            <div className="flex flex-col w-[40%]">
                                                <p className="mb-[10px]">{t('sub_district')}</p>
                                                <TextField placeholder={t('sub_district')} disabled={true} value={taxInfo?.location?.subDistrict ?? ''} />
                                            </div>
                                            <div className="flex flex-col w-[40%]">
                                                <p className="mb-[10px]">{t('province')}</p>
                                                <TextField placeholder={t('province')} disabled={true} value={taxInfo?.location?.province ?? ''} />
                                            </div>
                                            <Button onClick={() => setShowPickupLocation(true)} variant="contained">{t('select_village_location')}</Button>
                                        </div>
                                    </Box>
                                </div>
                                <div className="flex justify-end mt-[30px]" >
                                    <LoadingButton disabled={!areFieldsValid()} variant="contained" loading={saving} onClick={() => updateOrganisationTaxInformation()}>{t('save')}</LoadingButton>
                                </div>
                                <Dialog
                                    open={showPickUpLocation}
                                    onClose={() => setShowPickupLocation(false)}
                                    aria-labelledby="alert-dialog-title"
                                    aria-describedby="alert-dialog-description">
                                    <DialogContent>
                                        <LocationPicker client={props.client} onLocationSelected={(l) => {
                                            taxInfo.location = l
                                            setTaxInfo(TaxInformationModel.copy(taxInfo));
                                            setShowPickupLocation(false)
                                        }} />
                                    </DialogContent>
                                </Dialog>
                            </div>
                            : <></>
                    }
                </TabPanel>
                <TabPanel value={"3"}>
                    <OrgProfileGroup groups={groups} orgId={id} canSeeNam={org?.niaga_at_mill_enabled} />
                </TabPanel>
            </TabContext>
            <Loader isLoading={saving} />
        </div>
    )
}